import { useCallback, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { BundleCollectionHandle } from '../enums/CollectionHandle.enum';
import { Color } from '../enums/Color.enum';
import { Fit } from '../enums/Fit.enum';
import { ProductHandle } from '../enums/ProductHandle.enum';
import { DataLayerVariant } from '../models/DataLayer.model';
import { ViewVariant } from '../models/ShopifyVariant.model';
import { LongSize } from '../product_detail/components/size.enum';
import { allProductsState, shopifyState } from '../recoil/atoms';
import { BundleCollectionState, bundleCollectionState, BundleProductState } from '../recoil/selectors';
import { trackProductSizeChanged } from '../tracking/events';
import { useBundleConfig } from './useBundleConfig';

interface BundleSelectionResponse {
  bundleCollectionHandle: BundleCollectionHandle;
  selectedSwatch: string;
  setSelectedSwatch: (swatchName: string) => void;
  getSize: (productHandle: ProductHandle) => LongSize | undefined;
  setSize: (productHandle: ProductHandle, size: LongSize | undefined, confirmedForStepper: boolean) => void;
  getFit: (productHandle: ProductHandle) => Fit | undefined;
  setFit: (productHandle: ProductHandle, fit: Fit | undefined) => void;
  getColor: (productHandle: ProductHandle) => Color | undefined;
  isSoldOut: boolean;
  variantsWithQuantities: {
    variant: ViewVariant | undefined;
    quantity: number;
    productHandle: ProductHandle;
    index: number;
  }[];
  bundleColors: string;
}

export const useBundleSelection = (bundleCollectionHandle: BundleCollectionHandle): BundleSelectionResponse => {
  const [bundleState, setBundleState] = useRecoilState(bundleCollectionState(bundleCollectionHandle));
  const config = useBundleConfig(bundleCollectionHandle || BundleCollectionHandle.SilhouetteBundle);
  const allProducts = useRecoilValue(allProductsState);
  const shopify = useRecoilValue(shopifyState);

  const setSelectedSwatch = useCallback(
    (swatchName: string) => {
      setBundleState((prev) => {
        // Change the colors of the existing bundle state
        // to match the colors of the bundle config based
        // on the new swatch
        const existingProducts = prev.products;

        const configCombos = config.productColorCombos.find((pcc) => pcc.swatchTitle === swatchName)!;

        const newProducts = configCombos.colorCombos.map((cc, i) => {
          const product: BundleProductState = {
            ...existingProducts[i],
            quantity: cc.quantity,
            color: cc.color,
            productHandle: cc.productHandle,
          };

          return product;
        });

        const newState: BundleCollectionState = {
          ...prev,
          products: newProducts,
          selectedSwatch: swatchName,
        };

        return newState;
      });
    },
    [config.productColorCombos, setBundleState],
  );

  const getSize = useCallback(
    (productHandle: ProductHandle): LongSize | undefined => {
      return bundleState.products.find((p) => p.productHandle === productHandle)?.size;
    },
    [bundleState.products],
  );

  const setSize = useCallback(
    (productHandle: ProductHandle, newSize: LongSize | undefined, confirmedForStepper: boolean) => {
      const product = allProducts[productHandle];
      if (newSize && product) {
        const fit = getFit(productHandle);
        const color = getColor(productHandle);
        const variant = product.variants.find((v) => v.color === color && (!v.fit || v.fit === fit) && v.size === newSize);
        trackProductSizeChanged(shopify, product, variant as DataLayerVariant, color, newSize, fit);
      }
      setBundleState((prev) => {
        const updatedProducts = prev.products.reduce<BundleProductState[]>((acc, curr) => {
          if (curr.productHandle === productHandle) {
            return [...acc, { ...curr, size: newSize, confirmedForStepper }];
          } else {
            return [...acc, curr];
          }
        }, []);

        return { ...prev, products: updatedProducts };
      });
    },
    [setBundleState],
  );

  const getFit = useCallback(
    (productHandle: ProductHandle): Fit | undefined => {
      return bundleState.products.find((p) => p.productHandle === productHandle)?.fit;
    },
    [bundleState.products],
  );

  const setFit = useCallback(
    (productHandle: ProductHandle, newFit: Fit | undefined) => {
      setBundleState((prev) => {
        const updatedProducts = prev.products.reduce<BundleProductState[]>((acc, curr) => {
          if (curr.productHandle === productHandle) {
            acc.push({
              ...curr,
              fit: newFit,
            });
          } else {
            acc.push(curr);
          }

          return acc;
        }, []);

        return { ...prev, products: updatedProducts };
      });
    },
    [setBundleState],
  );

  // Note: This returns the first color found. It should only be used for display purposes when SOMETHING needs to be shown to the user
  // eg: During sizing tool or stepper, we might want to show a relevant color photo to the user.
  const getColor = useCallback(
    (productHandle: ProductHandle) =>
      config?.productColorCombos.find((pcc) => pcc.swatchTitle === bundleState.selectedSwatch)?.colorCombos.find((cc) => cc.productHandle === productHandle)
        ?.color,
    [bundleState.selectedSwatch, config?.productColorCombos],
  );

  const isSoldOut = useMemo(
    () =>
      bundleState.products.some((p) => {
        const product = allProducts[p.productHandle];
        const variant = product.variants.find((v) => v.color === p.color && (!v.fit || v.fit === p.fit) && v.size === p.size);
        return variant && !variant.available;
      }),
    [allProducts, bundleState.products],
  );

  const variantsWithQuantities = useMemo(() => {
    return config.productColorCombos
      .find((pcc) => pcc.swatchTitle === bundleState.selectedSwatch)!
      .colorCombos.map((cc, i) => {
        const { quantity, productHandle: handle, color } = cc;

        const fit = getFit(handle);
        const size = getSize(handle);
        const isConfirmed = bundleState.products[i].confirmedForStepper;

        const variant = isConfirmed ? allProducts[handle].variants.find((v) => v.color === color && v.size === size && (!v.fit || v.fit === fit)) : undefined;
        return { productHandle: handle, variant: variant?.available ? variant : undefined, quantity, index: i };
      });
  }, [allProducts, bundleState.products, bundleState.selectedSwatch, config.productColorCombos, getFit, getSize]);

  const bundleColors = useMemo(
    () =>
      config.productColorCombos
        .find((pcc) => pcc.swatchTitle === bundleState.selectedSwatch)!
        .colorCombos.map((cc) => cc.color)
        .join('/'),
    [bundleState.selectedSwatch, config.productColorCombos],
  );

  return {
    bundleCollectionHandle,
    getFit,
    setFit,
    selectedSwatch: bundleState.selectedSwatch,
    setSelectedSwatch,
    getSize,
    setSize,
    getColor,
    isSoldOut,
    variantsWithQuantities,
    bundleColors,
  };
};

interface BundleProductSelectionResponse {
  bundleCollectionHandle: BundleCollectionHandle;
  selectedSwatch: string;
  setSelectedSwatch: (swatchName: string) => void;
  size: LongSize | undefined;
  setSize: (size: LongSize | undefined, confirmedForStepper: boolean) => void;
  fit: Fit | undefined;
  setFit: (fit: Fit | undefined) => void;
  color: Color | undefined;
}

export const useBundleProductSelection = (
  bundleCollectionHandle: BundleCollectionHandle,
  productHandle: ProductHandle,
): BundleProductSelectionResponse | undefined => {
  const bundleSelection = useBundleSelection(bundleCollectionHandle);
  if (!bundleSelection || !bundleCollectionHandle) return undefined;
  const { getFit, setFit, getSize, setSize, selectedSwatch, setSelectedSwatch, getColor } = bundleSelection;
  return {
    bundleCollectionHandle,
    fit: getFit(productHandle),
    setFit: (newFit: Fit | undefined) => setFit(productHandle, newFit),
    size: getSize(productHandle),
    setSize: (newSize: LongSize | undefined, confirmedForStepper: boolean) => setSize(productHandle, newSize, confirmedForStepper),
    selectedSwatch,
    setSelectedSwatch,
    color: getColor(productHandle),
  };
};
